"use client";

import { useParams } from "next/navigation";
import { usePathname, type useRouter } from "@/i18n/navigation";
import {
  type BlogSectionPostPathname,
  postPathnameSection,
  sectionPostHref,
} from "@/lib/blog/sections";

type AppPathname = ReturnType<typeof usePathname>;
type AppHref = Parameters<ReturnType<typeof useRouter>["push"]>[0];
type DynamicPathname =
  | "/blog/[slug]"
  | BlogSectionPostPathname
  | "/wisdom/[collectionId]"
  | "/collection/[collectionId]"
  | "/recap/[recapId]";
type StaticPathname = Exclude<AppPathname, DynamicPathname>;
type Query = Record<string, string>;
type RouteParams = ReturnType<typeof useParams>;

function withQuery<P extends StaticPathname>(
  pathname: P,
  query: Query,
): { pathname: P; query: Query } {
  return { pathname, query };
}

function staticHref(
  pathname: StaticPathname,
  query: Query,
  hasQuery: boolean,
): AppHref {
  if (!hasQuery) {
    return pathname;
  }
  switch (pathname) {
    case "/":
      return withQuery(pathname, query);
    case "/pricing":
      return withQuery(pathname, query);
    case "/privacy":
      return withQuery(pathname, query);
    case "/terms":
      return withQuery(pathname, query);
    case "/dashboard":
      return withQuery(pathname, query);
    case "/auth":
      return withQuery(pathname, query);
    case "/login":
      return withQuery(pathname, query);
    case "/register":
      return withQuery(pathname, query);
    case "/cookies":
      return withQuery(pathname, query);
    case "/feature-requests":
      return withQuery(pathname, query);
    case "/bug-reports":
      return withQuery(pathname, query);
    case "/feature-requests/create":
      return withQuery(pathname, query);
    case "/bug-reports/create":
      return withQuery(pathname, query);
    case "/affiliate":
      return withQuery(pathname, query);
    case "/affiliate/claim":
      return withQuery(pathname, query);
    case "/affiliate/terms":
      return withQuery(pathname, query);
    case "/how-it-works":
      return withQuery(pathname, query);
    case "/about":
      return withQuery(pathname, query);
    case "/contact":
      return withQuery(pathname, query);
    case "/blog":
      return withQuery(pathname, query);
    case "/guides":
      return withQuery(pathname, query);
    case "/scenarios":
      return withQuery(pathname, query);
    case "/comparisons":
      return withQuery(pathname, query);
    case "/articles":
      return withQuery(pathname, query);
    case "/wisdom":
      return withQuery(pathname, query);
    case "/onboarding/signin":
      return withQuery(pathname, query);
    case "/onboarding/offer":
      return withQuery(pathname, query);
    case "/onboarding/how-to-use":
      return withQuery(pathname, query);
    case "/auth/mobile-callback":
      return withQuery(pathname, query);
    case "/youtube-summarizer":
      return withQuery(pathname, query);
    case "/youtube-summary-chatgpt":
      return withQuery(pathname, query);
    case "/youtube-transcript":
      return withQuery(pathname, query);
    case "/youtube-transcript-api":
      return withQuery(pathname, query);
    case "/dashboard/api":
      return withQuery(pathname, query);
    case "/dashboard/library":
      return withQuery(pathname, query);
    case "/dashboard/subscriptions":
      return withQuery(pathname, query);
    case "/notifications":
      return withQuery(pathname, query);
    case "/connectors":
      return withQuery(pathname, query);
    case "/connectors/consent":
      return withQuery(pathname, query);
    default: {
      const unreachable: never = pathname;
      return unreachable;
    }
  }
}

function stringParam(value: string | string[] | undefined): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

function dynamicHref(
  pathname: DynamicPathname,
  params: RouteParams,
  query: Query,
  hasQuery: boolean,
): AppHref | null {
  if (pathname === "/blog/[slug]") {
    const slug = stringParam(params.slug);
    if (!slug) return null;
    return hasQuery
      ? { pathname, params: { slug }, query }
      : { pathname, params: { slug } };
  }
  if (pathname === "/wisdom/[collectionId]") {
    const collectionId = stringParam(params.collectionId);
    if (!collectionId) return null;
    return hasQuery
      ? { pathname, params: { collectionId }, query }
      : { pathname, params: { collectionId } };
  }
  if (pathname === "/collection/[collectionId]") {
    const collectionId = stringParam(params.collectionId);
    if (!collectionId) return null;
    return hasQuery
      ? { pathname, params: { collectionId }, query }
      : { pathname, params: { collectionId } };
  }
  if (pathname === "/recap/[recapId]") {
    const recapId = stringParam(params.recapId);
    if (!recapId) return null;
    return hasQuery
      ? { pathname, params: { recapId }, query }
      : { pathname, params: { recapId } };
  }
  // The post page redirects a slug from another language to its translation.
  const section = postPathnameSection(pathname);
  const slug = stringParam(params.slug);
  if (!section || !slug) return null;
  const href = sectionPostHref(section, slug);
  return hasQuery ? { ...href, query } : href;
}

function isDynamicPathname(pathname: AppPathname): pathname is DynamicPathname {
  return (
    pathname === "/blog/[slug]" ||
    postPathnameSection(pathname) !== null ||
    pathname === "/wisdom/[collectionId]" ||
    pathname === "/collection/[collectionId]" ||
    pathname === "/recap/[recapId]"
  );
}

/**
 * Build a next-intl href for locale switching that includes dynamic route params.
 */
export function useLocaleSwitchHref(query: Query = {}): AppHref {
  const pathname = usePathname();
  const params = useParams();
  const hasQuery = Object.keys(query).length > 0;

  if (isDynamicPathname(pathname)) {
    return dynamicHref(pathname, params, query, hasQuery) ?? "/";
  }

  return staticHref(pathname, query, hasQuery);
}
